import React, { useState } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import SoapTemplateItem from '@/apps/latest/components/molecules/SoapTemplateItem'
import {
  ConfirmDialog,
  NotionButton,
  NotionTextField,
} from '@/apps/latest/components/portal/notion'
import KeywordInput from '@/apps/latest/components/molecules/KeywordInput'
import {
  SoapTemplateGroup,
  SoapType,
} from '@/features/internal/drugs/types/soapTemplate'
import { SECONDARY_ICON_BUTTON_STYLE } from '@/shared/constants/buttonStyles'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { PORTAL_TEXT } from '@/apps/latest/shared/styles/portalTypography'
import { RADIUS } from '@/shared/styles/radii'

interface SoapTemplateSectionProps {
  /**
   * SOAPテンプレートグループデータ
   */
  group: SoapTemplateGroup

  /**
   * 編集モードかどうか
   */
  editMode?: boolean

  /**
   * グループ名変更時に呼び出されるコールバック関数
   */
  onGroupNameChange: (groupId: string, name: string) => void

  /**
   * キーワード変更時に呼び出されるコールバック関数
   */
  onKeywordsChange: (groupId: string, keywords: string[]) => void

  /**
   * 新しいSOAP項目追加時に呼び出されるコールバック関数
   */
  onAddItem: (groupId: string, soapType: SoapType) => void

  /**
   * SOAP項目削除時に呼び出されるコールバック関数
   */
  onDeleteItem: (groupId: string, itemId: string) => void

  /**
   * SOAP項目内容変更時に呼び出されるコールバック関数
   */
  onItemContentChange: (
    groupId: string,
    itemId: string,
    content: string
  ) => void

  /**
   * グループ削除時に呼び出されるコールバック関数
   */
  onDeleteGroup: (groupId: string) => void
}

// フラットなカード枠 (Notion 流: 影なし・罫線 1 本)
const SectionCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  border: `1px solid ${colorTokens.border.subtle}`,
  borderRadius: RADIUS.md,
}))

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
}))

/**
 * SOAPテンプレートグループとその中の項目を管理するコンポーネント
 */
const SoapTemplateSection: React.FC<SoapTemplateSectionProps> = ({
  group,
  editMode = false,
  onGroupNameChange,
  onKeywordsChange,
  onAddItem,
  onDeleteItem,
  onItemContentChange,
  onDeleteGroup,
}) => {
  // SOAPタイプ選択メニュー用の状態
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [deleteGroupConfirmOpen, setDeleteGroupConfirmOpen] = useState(false)

  // メニュー開閉ハンドラー
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
  }

  // SOAP項目追加ハンドラー
  const handleAddItem = (soapType: SoapType) => {
    onAddItem(group.id, soapType)
    handleCloseMenu()
  }

  // 選択できるSOAP種別のリスト (素の日本語ラベル)
  const soapTypes: { type: SoapType; label: string }[] = [
    { type: 'S', label: '主観的情報' },
    { type: 'O', label: '客観的情報' },
    { type: 'A', label: '評価' },
    { type: 'OP', label: '観察計画' },
    { type: 'EP', label: '教育計画' },
    { type: 'CP', label: 'ケア計画' },
  ]

  return (
    <SectionCard>
      <SectionHeader>
        {editMode ? (
          <>
            <Box sx={{ flexGrow: 1 }}>
              <NotionTextField
                value={group.name}
                onChange={(e) => onGroupNameChange(group.id, e.target.value)}
                placeholder="グループ名"
                aria-label="SOAPテンプレートグループ名"
                autoFocus={!group.name}
              />
            </Box>
            <Tooltip title="グループを削除" placement="top">
              <IconButton
                size="small"
                onClick={() => setDeleteGroupConfirmOpen(true)}
                sx={{ ml: 1, ...SECONDARY_ICON_BUTTON_STYLE }}
                aria-label="グループを削除"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <Typography
            sx={{ ...PORTAL_TEXT.rowLabel, fontWeight: 600, flexGrow: 1 }}
          >
            {group.name}
          </Typography>
        )}
      </SectionHeader>
      {editMode && (
        <Box sx={{ mb: 3 }}>
          <KeywordInput
            keywords={group.keywords}
            onChange={(keywords) => onKeywordsChange(group.id, keywords)}
            placeholder="キーワードを入力してEnterまたはカンマで追加"
          />
        </Box>
      )}
      {!editMode && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ ...PORTAL_TEXT.cellMeta, mb: 0.5 }}>
            キーワード:
          </Typography>
          {group.keywords.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {group.keywords.map((keyword, index) => (
                <Typography
                  key={index}
                  variant="caption"
                  sx={{
                    backgroundColor: colorTokens.bg.muted,
                    color: colorTokens.fg.default,
                    px: 1,
                    py: 0.5,
                    borderRadius: `${RADIUS.md}px`,
                  }}
                >
                  {keyword}
                </Typography>
              ))}
            </Box>
          ) : (
            <Typography sx={PORTAL_TEXT.emptyHint}>(なし)</Typography>
          )}
        </Box>
      )}

      {group.templates.length > 0 ? (
        <Box sx={{ mb: 1 }}>
          {group.templates.map((item) => (
            <SoapTemplateItem
              key={item.id}
              item={item}
              onContentChange={(content) =>
                onItemContentChange(group.id, item.id!, content)
              }
              onDelete={() => onDeleteItem(group.id, item.id!)}
              editMode={editMode}
            />
          ))}
        </Box>
      ) : (
        !editMode && <Typography sx={PORTAL_TEXT.emptyHint}>(なし)</Typography>
      )}

      {editMode && (
        <>
          <NotionButton
            variant="ghost"
            startIcon={<AddIcon />}
            onClick={handleOpenMenu}
            sx={{ alignSelf: 'flex-start' }}
          >
            項目を追加
          </NotionButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleCloseMenu}
          >
            {soapTypes.map((soap) => (
              <MenuItem
                key={soap.type}
                onClick={() => handleAddItem(soap.type)}
              >
                <Typography sx={PORTAL_TEXT.cellBody}>
                  <strong>{soap.type}</strong>
                  <Typography
                    component="span"
                    sx={{ ...PORTAL_TEXT.cellSecondary, ml: 1 }}
                  >
                    {soap.label}
                  </Typography>
                </Typography>
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      <ConfirmDialog
        open={deleteGroupConfirmOpen}
        onClose={() => setDeleteGroupConfirmOpen(false)}
        onConfirm={() => {
          setDeleteGroupConfirmOpen(false)
          onDeleteGroup(group.id)
        }}
        title="テンプレートグループの削除"
        message={`「${group.name}」を削除しますか？`}
        confirmLabel="削除する"
        variant="danger"
      />
    </SectionCard>
  )
}

export default SoapTemplateSection
